import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { plDisplayCurrencyType, type PlStatementRowView } from '@/api/map/plStatement'
import {
  lineSessionAvgOdds,
  lineSessionMatchedStake,
  lineSessionPlacedOdds,
} from '@/lib/lineSessionGameDetailOdds'

export type GetOneAccountResponse = {
  value: boolean
  data?: Record<string, unknown>
}

/** Lab `bet.betUpdate[]` row — sportsbook-detail Details eye → Bet Detail modal. */
export type OneAccountBetUpdateView = {
  matchedAt: string | null
  averagePriceMatched: number | null
  amountExecuted: number
  amountPending: number
  amountVoided: number
  amountCancelled: number
}

export type OneAccountBetView = {
  id: string
  placedAt: string | null
  matchedAt: string | null
  type: string
  status: string
  selectionName: string
  /** Lab line-session / fancy line (orders.runs) — Game Detail "Run". */
  runs: number | null
  odds: number | null
  avgOdds: number | null
  stake: number
  matchedStake: number
  pnl: number | null
  /** Lab Invalid Bet invalidate reason (sportsbook-detail Comment / View). */
  reason?: string
  /** Lab bet fields used by PL View Graph (archive surveillance). */
  eventType?: string
  eventId?: string
  marketId?: string
  betCategory?: string
  userName?: string
  currencyType?: string
  /** Lab fill history — eye only when non-empty. */
  betUpdate: OneAccountBetUpdateView[]
}

/** Lab Kings AR roulette / live-casino outcome row. */
export type ArOutcomeView = {
  number: string
  type: string
  color: string
}

/** Lab `arbetDetails.participants[0].bets[]` row (stake/payout already × playerRate). */
export type ArCasinoBetView = {
  id: string
  code: string
  description: string
  stake: number
  payout: number
  transactionId: string
  placedOn: string | null
}

/** Lab `ARbetDetails` when Agent-API returns the Kings AR round payload. */
export type ArBetDetailsView = {
  gameType: string
  tableName: string
  outcomes: ArOutcomeView[]
  bets: ArCasinoBetView[]
  transactionId: string
}

/** Lab SoftGamings Fundist `outputObject.actions[]` (Crash / live-casino-detail). */
export type FundistActionView = {
  id: string
  actionTime: string
  actionAmount: number
  actionAmountBonus: number
  actionAmountReal: number
  actionId: string
  type: string
}

/** Lab `fundistDetails` when CM-API Crash-Adapter fills SoftGamings Stats/GameDetails. */
export type FundistDetailsView = {
  gameDesc: string
  gameId: string
  startTime: string
  endTime: string
  actions: FundistActionView[]
}

/** Lab Card Game runner hand (`cards[0].marketRunner[]`). */
export type CardGameRunnerView = {
  name: string
  status: string
  cards: string[]
}

/**
 * Lab `Account.cards[0]` — vendor market snapshot for sportsbook-detail Card section.
 * `indexCard` mixes numeric index tokens (text) and card codes (SVG).
 */
export type CardGameMarketView = {
  roundId: string
  indexCard: Array<string | number>
  marketRunner: CardGameRunnerView[]
}

export type OneAccountView = {
  id: string
  gameName: string
  eventName: string
  marketName: string
  marketId: string
  /** Lab event id for archive View Graph (`bqGetBetsForSurv`). */
  eventId: string
  /** Lab sport / eventType for archive View Graph. */
  eventType: string
  /**
   * Lab first-executed-bet `betCategory` — View Graph hides Line Session /
   * Bookmaker / Custom Session (sportsbook-detail).
   */
  betCategory: string
  subGame: string
  username: string
  winLoss: string
  winAmt: number
  commissionAmt: number
  netAmount: number
  winner: string
  resultTime: string | null
  /** Lab Game Detail "Round Id" (sports marketId; Card Game argame/round/market). */
  roundId: string
  isCardGame: boolean
  isRollback: boolean
  /** Lab Invalid Bets applied on this settlement — hide Invalid Bets actions. */
  isInvalid?: boolean
  /** Lab Invalidate P&L — hide Invalidate P&L (keep separate from isInvalid). */
  isPNLInvalid: boolean
  isLiveCasino: boolean
  /** True when settlement bets are SoftGamings Fundist (Crash). */
  isFundist: boolean
  /** Lab line session / overs–runs line — Game Detail shows Run column. */
  isLineSession: boolean
  /** Settlement currency (INR / POINTS) — prefer over page viewer currency. */
  currencyType?: string
  bets: OneAccountBetView[]
  /** Present when Live Casino getOneAccount includes ARbetDetails. */
  arBetDetails?: ArBetDetailsView
  /** Present when Live Casino getOneAccount includes fundistDetails. */
  fundistDetails?: FundistDetailsView
  /** Card Game only — Lab `Account.cards` for result hand images. */
  cardMarkets: CardGameMarketView[]
  unavailable?: 'forbidden' | 'empty' | 'live-casino'
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value)
}

const CARD_GAME_LABEL = /^(teenpatti|cardgame|card game|poker|andar|bahar)$/i

/**
 * Lab Game Detail Round Id.
 * Sports: Betfair/BM marketId.
 * Card Game: operator round / argameId, skipping game-name stamps like "teenpatti".
 */
export function gameDetailRoundId(opts: {
  isCardGame?: boolean
  roundId?: string | null
  argameId?: string | null
  marketId?: string | null
}): string {
  const market = String(opts.marketId ?? '').trim()
  const round = String(opts.roundId ?? '').trim() || String(opts.argameId ?? '').trim()
  if (!opts.isCardGame) return market
  if (round && !CARD_GAME_LABEL.test(round)) return round
  return market
}

function firstRoundIdFromBets(bets: unknown): string {
  if (!Array.isArray(bets)) return ''
  for (const b of bets) {
    const recB = rec(b)
    const info = rec(recB.betInfo)
    const id = str(recB.roundId) || str(info.roundId) || str(info.round_id)
    if (id) return id
  }
  return ''
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function iso(value: unknown): string | null {
  const s = str(value)
  return s || null
}

/** Lab multiplies stake/payout by playerRate when present; otherwise leave as-is. */
export function arMoney(amount: unknown, playerRate: number): number {
  const base = num(amount)
  return playerRate > 0 ? base * playerRate : base
}

export function parseArOutcomes(raw: unknown): ArOutcomeView[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((o): o is Record<string, unknown> => Boolean(o) && typeof o === 'object')
    .map((o) => ({
      number: str(o.number),
      type: str(o.type),
      color: str(o.color),
    }))
}

export function parseArCasinoBets(raw: unknown, playerRate: number): ArCasinoBetView[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((b): b is Record<string, unknown> => Boolean(b) && typeof b === 'object')
    .map((b, index) => ({
      id: str(b.transactionId) || str(b.code) || `ar-bet-${index}`,
      code: str(b.code),
      description: str(b.description),
      stake: arMoney(b.stake, playerRate),
      payout: arMoney(b.payout, playerRate),
      transactionId: str(b.transactionId),
      placedOn: iso(b.placedOn),
    }))
}

/**
 * Lab `ARbetDetails` → view. Returns null when missing or empty.
 * Lab uses `!_.isEmpty(ARbetDetails)` — keep gameType-only Evolution payloads.
 */
export function parseArBetDetails(raw: unknown, playerRate = 0): ArBetDetailsView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const ar = raw as Record<string, unknown>
  const table = rec(ar.table)
  const result = rec(ar.result)
  const participants = Array.isArray(ar.participants) ? ar.participants : []
  const first = rec(participants[0])
  const tableName = str(table.name)
  const gameType = str(ar.gameType) || tableName
  const outcomes = parseArOutcomes(result.outcomes)
  const bets = parseArCasinoBets(first.bets, playerRate)
  if (!tableName && !gameType && outcomes.length === 0 && bets.length === 0) return null
  return {
    gameType,
    tableName,
    outcomes,
    bets,
    transactionId: bets[0]?.transactionId ?? '',
  }
}

/** Lab `card` directive: `img/cards/{CODE}.svg` (missing → NONE). */
export function cardImageSrc(code: unknown): string {
  const c = String(code ?? 'NONE').trim().toUpperCase() || 'NONE'
  return `/img/cards/${encodeURIComponent(c)}.svg`
}

/** Angular `isNaN` filter: numeric tokens render as text; card codes as SVGs. */
export function isCardCodeToken(token: unknown): boolean {
  if (token == null || token === '') return false
  return Number.isNaN(Number(token))
}

function parseIndexCard(raw: unknown): Array<string | number> {
  if (!Array.isArray(raw)) return []
  return raw.map((t) => {
    if (typeof t === 'number') return t
    const s = str(t)
    const n = Number(s)
    return s !== '' && Number.isFinite(n) && String(n) === s.trim() ? n : s
  })
}

function parseMarketRunners(raw: unknown): CardGameRunnerView[] {
  let list: unknown = raw
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list)
    } catch {
      return []
    }
  }
  if (!Array.isArray(list)) return []
  return list
    .filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === 'object')
    .map((r) => ({
      name: str(r.name),
      status: str(r.status),
      cards: Array.isArray(r.cards) ? r.cards.map((c) => str(c)).filter(Boolean) : [],
    }))
}

/**
 * Lab `Account.cards` (= vendor market) → Card Game result markets.
 * Accepts array, single market object, or JSON string (CM-API / PG jsonb).
 */
export function parseCardGameCards(raw: unknown): CardGameMarketView[] {
  let value: unknown = raw
  if (typeof value === 'string') {
    const s = value.trim()
    if (!s) return []
    try {
      value = JSON.parse(s)
    } catch {
      return []
    }
  }
  const list = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? [value]
      : []
  return list
    .filter((m): m is Record<string, unknown> => Boolean(m) && typeof m === 'object')
    .map((m) => ({
      roundId: str(m.roundId ?? m.round_id),
      indexCard: parseIndexCard(m.indexCard ?? m.index_card),
      marketRunner: parseMarketRunners(m.marketRunner ?? m.market_runner),
    }))
}

/** Lab SoftGamings `fundistDetails.outputObject` → view. */
export function parseFundistDetails(raw: unknown): FundistDetailsView | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const root = raw as Record<string, unknown>
  const out =
    root.outputObject && typeof root.outputObject === 'object' && !Array.isArray(root.outputObject)
      ? (root.outputObject as Record<string, unknown>)
      : root.i_gameid != null || root.i_gamedesc != null
        ? root
        : null
  if (!out) return null
  const gameDesc = str(out.i_gamedesc)
  const gameId = str(out.i_gameid)
  const actionsRaw = Array.isArray(out.actions) ? out.actions : []
  const actions = actionsRaw
    .filter((a): a is Record<string, unknown> => Boolean(a) && typeof a === 'object')
    .map((a, index) => ({
      id: str(a.i_actionid) || `fundist-action-${index}`,
      actionTime: str(a.action_time),
      actionAmount: num(a.action_amount),
      actionAmountBonus: num(a.action_amount_bonus),
      actionAmountReal: num(a.action_amount_real),
      actionId: str(a.i_actionid),
      type: str(a.type),
    }))
  if (!gameDesc && !gameId && actions.length === 0) return null
  return {
    gameDesc,
    gameId,
    startTime: str(out.start_time),
    endTime: str(out.end_time),
    actions,
  }
}

/** Lab `gameDetails` keeps bets that actually executed. */
export function executedBets(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((bet): bet is Record<string, unknown> => {
    if (!bet || typeof bet !== 'object') return false
    const row = bet as Record<string, unknown>
    return num(row.amountExecuted) > 0 || num(row.rtAmountExecuted) > 0
  })
}

/** Lab / orders line price — runs on the bet or nested executedData. */
function betRuns(raw: Record<string, unknown>): number | null {
  const executed = rec(raw.executedData)
  const n = num(raw.runs ?? executed.runs ?? raw.line ?? executed.line)
  return n > 0 && Number.isFinite(n) ? n : null
}

function isLineSessionBet(raw: Record<string, unknown>): boolean {
  const executed = rec(raw.executedData)
  const betType = str(raw.betType).toLowerCase().replace(/[_-]/g, '')
  const evenMoney =
    num(raw.averagePriceMatched) === 2 ||
    num(executed.odds) === 2 ||
    num(raw.betRate) === 2 ||
    num(raw.odds) === 2
  return (
    betType === 'lineladder' ||
    betType === 'linesession' ||
    Boolean(raw.isLineSession) ||
    Boolean(raw.is_line_session) ||
    (betRuns(raw) != null && evenMoney)
  )
}

/**
 * Lab lineLadder Game Detail (#5783): Odds Placed / Avg (full vs partial) /
 * Matched Stake — not even-money 2.
 */
function lineSessionOddsColumns(raw: Record<string, unknown>): {
  odds: number | null
  avgOdds: number | null
  matchedStake: number
} | null {
  if (!isLineSessionBet(raw)) return null
  const executed = rec(raw.executedData)
  const input = {
    type: str(raw.type),
    stake: num(raw.stake),
    matchedAmt: num(raw.matchedAmt),
    amountExecuted: num(raw.amountExecuted),
    amountToBePlaced: num(raw.amountToBePlaced ?? raw.amountToBePlacedOnBetfair),
    runs: betRuns(raw),
    odds: num(raw.odds),
    betRate: num(raw.betRate),
    averagePriceMatched: num(raw.averagePriceMatched),
    betfairLimitOrderPrice: num(raw.betfairLimitOrderPrice),
    executedOdds: num(executed.odds),
  }
  return {
    odds: lineSessionPlacedOdds(input),
    avgOdds: lineSessionAvgOdds(input),
    matchedStake: lineSessionMatchedStake(input),
  }
}

/** Lab `bet.betUpdate` → Bet Detail modal rows. */
export function parseBetUpdate(raw: unknown): OneAccountBetUpdateView[] {
  if (!Array.isArray(raw) || !raw.length) return []
  return raw
    .filter((u): u is Record<string, unknown> => Boolean(u) && typeof u === 'object' && !Array.isArray(u))
    .map((u) => ({
      matchedAt: iso(u.matchedTime ?? u.matched_time ?? u.matchTime),
      averagePriceMatched: (() => {
        const n = num(u.averagePriceMatched ?? u.average_price_matched)
        return n > 0 ? n : null
      })(),
      amountExecuted: num(u.amountExecuted ?? u.amount_executed),
      amountPending: num(u.amountPending ?? u.amount_pending),
      amountVoided: num(u.amountVoided ?? u.amount_voided),
      amountCancelled: num(u.amountCancelled ?? u.amount_cancelled),
    }))
}

export function betFromLab(raw: Record<string, unknown>, index: number): OneAccountBetView {
  const executed = rec(raw.executedData)
  const player = rec(raw.player)
  const runs = betRuns(raw)
  const lineCols = lineSessionOddsColumns(raw)
  const odds = lineCols
    ? lineCols.odds
    : num(executed.odds || raw.betRate || raw.averagePriceMatched)
  const avgOdds = lineCols
    ? lineCols.avgOdds
    : num(raw.averagePriceMatched) || null
  const pnlRaw = raw.memberNet ?? raw.profitAmt ?? raw.pnlAmount ?? player.amount
  const status = str(raw.betWinStatus || raw.status)
  const betReason = str(raw.reason).trim()
  return {
    id: str(raw._id) || `bet-${index}`,
    placedAt: iso(raw.placedDate),
    matchedAt: iso(raw.matchedTime),
    type: str(raw.type).toUpperCase() || 'BACK',
    status,
    selectionName: str(raw.selectionName),
    runs,
    odds: odds != null && Number.isFinite(odds) && odds > 0 ? odds : null,
    avgOdds,
    stake: num(raw.stake),
    matchedStake: lineCols
      ? lineCols.matchedStake
      : num(raw.amountExecuted || raw.stake),
    pnl: pnlRaw == null || pnlRaw === '' ? null : num(pnlRaw),
    reason: betReason || undefined,
    eventType: str(raw.eventType) || undefined,
    eventId: str(raw.eventId) || undefined,
    marketId: str(raw.marketId) || undefined,
    betCategory: str(raw.betCategory) || undefined,
    userName: str(raw.userName || player.username || player.name) || undefined,
    currencyType: str(raw.currencyType) || undefined,
    betUpdate: parseBetUpdate(raw.betUpdate ?? raw.bet_update),
  }
}

export function fromGetOneAccount(
  response: GetOneAccountResponse,
  row: PlStatementRowView,
): OneAccountView {
  const data = rec(response.data)
  if (!response.value || !str(data._id)) {
    return {
      ...fixtureOneAccount(row),
      bets: [],
      unavailable: 'empty',
    }
  }

  const player = rec(data.player)
  const rawGameName = str(data.gameName) || row.game
  const isCrashGame = rawGameName === 'Crash Game'
  const isFundist = Boolean(data.isFundist) || isCrashGame
  const gameName = isCrashGame ? 'Crash Game' : rawGameName
  const isLiveCasino = rawGameName === 'Live Casino' || isCrashGame
  const isEvoCasino = rawGameName === 'Live Casino' && !isFundist
  const playerRate = num(data.playerRate)
  const arBetDetails = isEvoCasino
    ? parseArBetDetails(data.ARbetDetails ?? data.arbetDetails, playerRate)
    : null
  // Live Casino / Crash Game may carry fundistDetails even before isFundist is stamped.
  const fundistDetails =
    isFundist || isLiveCasino ? parseFundistDetails(data.fundistDetails) : null
  const sportsBets = executedBets(data.bets).map(betFromLab)
  const firstBet = sportsBets[0]
  const tableName = arBetDetails?.tableName || fundistDetails?.gameDesc || ''
  const marketName = str(data.marketName) || row.marketName
  const rawSubGame = str(data.subGame)
  const subGameClean = !rawSubGame || rawSubGame === 'None' ? '' : rawSubGame
  const isCardGame = Boolean(data.isCardGame) || row.game === 'Card Game'
  const lineLabel = `${marketName} ${str(data.eventName)} ${row.eventName} ${row.marketName}`
  const isLineSession =
    Boolean(data.isLineSession) ||
    Boolean(data.is_line_session) ||
    /line\s*session|overs\s*line|runs\s*line|\bline\b/i.test(lineLabel) ||
    sportsBets.some((b) => b.runs != null)
  const cardMarkets = isCardGame ? parseCardGameCards(data.cards) : []
  const currencyType = plDisplayCurrencyType(
    str(data.currencyType).trim() ||
      firstBet?.currencyType?.trim() ||
      row.currencyType?.trim() ||
      undefined,
    {
      isBookmaker:
        Boolean(data.isBookmaker) ||
        row.game === 'SportsBook - Bookmaker' ||
        /bookmaker/i.test(marketName),
      isFancy: Boolean(data.isFancy) || row.game === 'Fancy' || /fancy/i.test(marketName),
      isCardGame,
      gameName,
      marketName,
    },
  )
  const marketId = str(data.marketId) || firstBet?.marketId || row.marketId
  const eventId = firstBet?.eventId || str(data.eventId) || row.eventId
  const eventType =
    firstBet?.eventType || subGameClean || str(data.eventType) || row.sport
  const betCategory = firstBet?.betCategory || str(data.betCategory)
  // Lab Round Id is cards[0].roundId when present (#107 API + sportsbook-detail).
  const roundId = gameDetailRoundId({
    isCardGame,
    roundId:
      str(data.roundId) ||
      cardMarkets[0]?.roundId ||
      firstRoundIdFromBets(data.bets),
    argameId: str(data.argameId),
    marketId,
  })

  return {
    id: str(data._id),
    gameName,
    eventName: str(data.eventName) || row.eventName,
    marketName,
    marketId,
    eventId,
    eventType,
    betCategory,
    // Don't copy marketName into subGame — header shows both and SoftGamings
    // stamps leave sub_game='None', so that made phoenixfirepowerreels appear twice.
    subGame: tableName || subGameClean || (isLiveCasino ? 'Casino' : '') || row.sport,
    username:
      str(player.username || player.name || data.playerName) ||
      firstBet?.userName ||
      row.memberName,
    winLoss: str(data.type) || sportsBets[0]?.status || '',
    winAmt: num(data.winAmt ?? player.winAmt ?? player.winAmount),
    commissionAmt: num(
      data.commissionAmt ?? rec(player.commission).netCommission ?? row.commission,
    ),
    netAmount: num(data.netAmount ?? data.netMarketTotal ?? player.netAmt ?? row.memberPnl),
    winner: str(data.winningHorseName),
    resultTime: iso(data.createdAt) || row.settledAt,
    roundId,
    isCardGame,
    isRollback: Boolean(data.isRollback) || Boolean(data.isPNLRollBack),
    isInvalid: Boolean(data.isInvalid),
    // Lab: do not OR Invalid Bet flags into isPNLInvalid.
    isPNLInvalid: Boolean(data.isPNLInvalid),
    isLiveCasino,
    isFundist,
    isLineSession,
    currencyType,
    bets: sportsBets,
    arBetDetails: arBetDetails ?? undefined,
    fundistDetails: fundistDetails ?? undefined,
    cardMarkets,
    // Vendor AR/Fundist payloads may be empty (adapter miss). Still show Lab settlement
    // header fields rather than a dead-end "not available" state.
    unavailable: undefined,
  }
}

export function fixtureOneAccount(row: PlStatementRowView): OneAccountView {
  return {
    id: row.id,
    gameName: row.game,
    eventName: row.eventName,
    marketName: row.marketName,
    marketId: row.marketId,
    eventId: row.eventId,
    eventType: row.sport,
    betCategory: '',
    roundId: row.marketId,
    subGame: row.sport,
    username: row.memberName,
    winLoss: row.memberPnl >= 0 ? 'WON' : 'LOST',
    winAmt: Math.abs(row.memberPnl),
    commissionAmt: row.commission,
    netAmount: row.memberPnl,
    winner: '',
    resultTime: row.settledAt,
    isCardGame: row.game === 'Card Game',
    isRollback: false,
    isPNLInvalid: false,
    isLiveCasino: row.game === 'Live Casino',
    isFundist: false,
    isLineSession: /line/i.test(`${row.marketName} ${row.eventName}`),
    currencyType: row.currencyType,
    bets: [
      {
        id: `${row.id}-bet`,
        placedAt: row.settledAt,
        matchedAt: row.settledAt,
        type: 'BACK',
        status: row.memberPnl >= 0 ? 'WON' : 'LOST',
        selectionName: row.marketName,
        runs: null,
        odds: null,
        avgOdds: null,
        stake: row.turnover || 0,
        matchedStake: row.turnover || 0,
        pnl: row.memberPnl,
        eventType: row.sport,
        eventId: row.eventId,
        marketId: row.marketId,
        userName: row.memberName,
        currencyType: row.currencyType,
        betUpdate: [],
      },
    ],
    cardMarkets: [],
    unavailable: row.game === 'Live Casino' ? 'live-casino' : undefined,
  }
}

/** Lab `Account/getOneAccount` — CM-API `POST /v1/account/getOneAccount`. */
export async function getOneAccount(id: string): Promise<GetOneAccountResponse> {
  return cmApi.getOneAccount(authHeaders(), { _id: id })
}
